/**
 * @CODE:SPEC-ACC-001-API-001
 * Accommodation API Layer
 *
 * Core API functions for accommodation search and filtering:
 * - searchAccommodations: Main search with filters, sorting, pagination
 * - searchNearCourse: Location-based search using PostGIS ST_DWithin
 * - Error handling and data transformation
 */

import { getSupabaseClient } from "@/lib/supabase";
import type {
  AccommodationWithDetails,
  AccommodationFilters,
  AccommodationSearchResponse,
  AccommodationError,
} from "@/features/accommodation/types";

/**
 * Default pagination limit
 */
const DEFAULT_LIMIT = 12;
const DEFAULT_PAGE = 1;

/**
 * Search accommodations with filters, sorting, and pagination
 *
 * Joins data from:
 * - accommodations (main table)
 * - accommodation_types (id, name)
 * - accommodation_images (main photo only: is_main_photo = true)
 *
 * @param filters - Search and filter parameters
 * @returns Paginated accommodation results
 *
 * @example
 * ```
 * const results = await searchAccommodations({
 *   province: '강원도',
 *   priceFrom: 50000,
 *   priceTo: 200000,
 *   sortBy: 'price',
 *   sortOrder: 'asc',
 *   page: 1,
 *   limit: 12
 * });
 * ```
 */
export async function searchAccommodations(
  filters: AccommodationFilters = {},
): Promise<AccommodationSearchResponse> {
  try {
    const client = getSupabaseClient();
    const page = filters.page ?? DEFAULT_PAGE;
    const limit = filters.limit ?? DEFAULT_LIMIT;
    const offset = (page - 1) * limit;

    // Build the base query with joins
    let query = client.from("accommodations").select(
      `
        accommodation_id:id,
        name,
        province,
        district,
        address,
        latitude,
        longitude,
        price_from,
        description,
        contact_phone:phone,
        website:homepage_url,
        map_url,
        amenities,
        accommodation_type:accommodation_types(id, name),
        accommodation_images!left(
          image_id:id,
          accommodation_id,
          image_url,
          is_main_photo
        )
      `,
      { count: "exact" },
    );

    // Filter to get only main images
    query = query.eq("accommodation_images.is_main_photo", true);

    // Apply filters
    if (filters.province) {
      query = query.eq("province", filters.province);
    }

    if (filters.district) {
      query = query.eq("district", filters.district);
    }

    if (filters.accommodation_type) {
      query = query.eq(
        "accommodation_type.name",
        filters.accommodation_type,
      );
    }

    if (filters.search) {
      // Search in name, description, address
      query = query.or(
        `name.ilike.%${filters.search}%,description.ilike.%${filters.search}%,address.ilike.%${filters.search}%`,
      );
    }

    if (filters.priceFrom !== undefined) {
      query = query.gte("price_from", filters.priceFrom);
    }

    if (filters.priceTo !== undefined) {
      query = query.lte("price_from", filters.priceTo);
    }

    if (filters.has_pickup) {
      query = query.eq("has_pickup", true);
    }

    // Apply sorting
    const sortBy = filters.sortBy ?? "name";
    const sortOrder = filters.sortOrder ?? "asc";
    query = query.order(sortBy, { ascending: sortOrder === "asc" });

    // Apply pagination
    query = query.range(offset, offset + limit - 1);

    // Execute query
    const { data, error, count } = await query;

    if (error) {
      console.error("Accommodation search error:", error);
      throw new Error(`Failed to search accommodations: ${error.message}`);
    }

    const totalPages = Math.ceil((count ?? 0) / limit);

    // Transform data: extract main_image from accommodation_images array
    const transformedData = (data ?? []).map((item: any) => ({
      ...item,
      main_image: Array.isArray(item.accommodation_images)
        ? item.accommodation_images[0]
        : item.accommodation_images,
      accommodation_images: undefined, // Remove the array field
    }));

    return {
      data: transformedData as AccommodationWithDetails[],
      total: count ?? 0,
      page,
      limit,
      totalPages,
    };
  } catch (error) {
    console.error("Error in searchAccommodations:", error);
    throw error;
  }
}

/**
 * Search accommodations near a course location using PostGIS
 *
 * Uses PostGIS ST_DWithin function to find accommodations within a radius
 * of a given coordinate (latitude, longitude).
 *
 * Combines location-based search with optional filters (type, price, etc.)
 *
 * @param filters - Must include latitude, longitude, radiusKm
 * @returns Paginated accommodation results
 *
 * @example
 * ```
 * const results = await searchNearCourse({
 *   latitude: 37.7749,
 *   longitude: -122.4194,
 *   radiusKm: 10,
 *   accommodation_type: '펜션',
 *   sortBy: 'price',
 *   page: 1,
 *   limit: 12
 * });
 * ```
 */
export async function searchNearCourse(
  filters: AccommodationFilters & {
    latitude: number;
    longitude: number;
    radiusKm: number;
  },
): Promise<AccommodationSearchResponse> {
  try {
    const client = getSupabaseClient();
    const page = filters.page ?? DEFAULT_PAGE;
    const limit = filters.limit ?? DEFAULT_LIMIT;
    const offset = (page - 1) * limit;

    // Build the base query
    let query = client.from("accommodations").select(
      `
        accommodation_id:id,
        name,
        province,
        district,
        address,
        latitude,
        longitude,
        price_from,
        description,
        contact_phone:phone,
        website:homepage_url,
        map_url,
        amenities,
        accommodation_type:accommodation_types(id, name),
        accommodation_images!left(
          image_id:id,
          accommodation_id,
          image_url,
          is_main_photo
        )
      `,
      { count: "exact" },
    );

    // Filter by main images only
    query = query.eq("accommodation_images.is_main_photo", true);

    // Apply simple latitude/longitude range filter for performance
    // This creates a bounding box around the search point
    const latDelta = filters.radiusKm / 111; // Approximate km to degrees
    const lngDelta = filters.radiusKm / (111 * Math.cos(filters.latitude * Math.PI / 180));

    query = query
      .gte("latitude", filters.latitude - latDelta)
      .lte("latitude", filters.latitude + latDelta)
      .gte("longitude", filters.longitude - lngDelta)
      .lte("longitude", filters.longitude + lngDelta);

    // Apply additional filters
    if (filters.accommodation_type) {
      query = query.eq(
        "accommodation_type.name",
        filters.accommodation_type,
      );
    }

    if (filters.priceFrom !== undefined) {
      query = query.gte("price_from", filters.priceFrom);
    }

    if (filters.priceTo !== undefined) {
      query = query.lte("price_from", filters.priceTo);
    }

    if (filters.has_pickup) {
      query = query.eq("has_pickup", true);
    }

    // Apply sorting
    const sortBy = filters.sortBy ?? "name";
    const sortOrder = filters.sortOrder ?? "asc";
    query = query.order(sortBy, { ascending: sortOrder === "asc" });

    // Apply pagination
    query = query.range(offset, offset + limit - 1);

    // Execute query
    const { data, error, count } = await query;

    if (error) {
      console.error("Location-based accommodation search error:", error);
      throw new Error(
        `Failed to search accommodations near course: ${error.message}`,
      );
    }

    const totalPages = Math.ceil((count ?? 0) / limit);

    // Transform data: extract main_image from accommodation_images array
    const transformedData = (data ?? []).map((item: any) => ({
      ...item,
      main_image: Array.isArray(item.accommodation_images)
        ? item.accommodation_images[0]
        : item.accommodation_images,
      accommodation_images: undefined, // Remove the array field
    }));

    return {
      data: transformedData as AccommodationWithDetails[],
      total: count ?? 0,
      page,
      limit,
      totalPages,
    };
  } catch (error) {
    console.error("Error in searchNearCourse:", error);
    throw error;
  }
}

/**
 * Get single accommodation by ID with full details
 *
 * @param accommodationId - Accommodation ID (UUID)
 * @returns Complete accommodation data with type and images
 */
export async function getAccommodationById(
  accommodationId: string,
): Promise<AccommodationWithDetails> {
  try {
    const client = getSupabaseClient();

    const { data, error } = await client
      .from("accommodations")
      .select(
        `
        accommodation_id:id,
        name,
        province,
        district,
        address,
        latitude,
        longitude,
        price_from,
        description,
        contact_phone:phone,
        website:homepage_url,
        map_url,
        amenities,
        trail_id,
        course_id,
        trail_name,
        course_number,
        distance_km,
        accommodation_type:accommodation_types(id, name),
        accommodation_images!left(
          image_id:id,
          accommodation_id,
          image_url,
          is_main_photo
        )
      `,
      )
      .eq("id", accommodationId)
      .eq("accommodation_images.is_main_photo", true)
      .single();

    if (error) {
      console.error("Failed to fetch accommodation:", error);
      throw new Error(`Failed to fetch accommodation: ${error.message}`);
    }

    if (!data) {
      throw new Error(`Accommodation ${accommodationId} not found`);
    }

    // Transform data: extract main_image from accommodation_images array
    const transformedData = {
      ...data,
      main_image: Array.isArray(data.accommodation_images)
        ? data.accommodation_images[0]
        : data.accommodation_images,
      accommodation_images: undefined,
    };

    return transformedData as AccommodationWithDetails;
  } catch (error) {
    console.error("Error in getAccommodationById:", error);
    throw error;
  }
}

/**
 * Get all accommodation types
 *
 * Used to populate filter dropdowns/selects
 *
 * @returns List of accommodation types
 */
export async function getAccommodationTypes(): Promise<
  Array<{ id: number; name: string }>
> {
  try {
    const client = getSupabaseClient();

    const { data, error } = await client
      .from("accommodation_types")
      .select("id, name")
      .order("type_name");

    if (error) {
      console.error("Failed to fetch accommodation types:", error);
      throw new Error(`Failed to fetch accommodation types: ${error.message}`);
    }

    return data ?? [];
  } catch (error) {
    console.error("Error in getAccommodationTypes:", error);
    throw error;
  }
}

/**
 * Get all unique provinces with accommodation count
 *
 * Used to populate province filter
 *
 * @returns List of provinces with count of accommodations
 */
export async function getProvinces(): Promise<
  Array<{ province: string; count: number }>
> {
  try {
    const client = getSupabaseClient();

    const { data, error } = await client
      .from("accommodations")
      .select("province")
      .returns<Array<{ province: string }>>();

    if (error) {
      console.error("Failed to fetch provinces:", error);
      throw new Error(`Failed to fetch provinces: ${error.message}`);
    }

    // Count by province
    const provinceCounts = (data ?? []).reduce(
      (acc, { province }) => {
        acc[province] = (acc[province] ?? 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    );

    return Object.entries(provinceCounts)
      .map(([province, count]) => ({ province, count }))
      .sort((a, b) => a.province.localeCompare(b.province));
  } catch (error) {
    console.error("Error in getProvinces:", error);
    throw error;
  }
}

/**
 * Get all unique districts for a given province
 *
 * Used to populate district filter based on selected province
 *
 * @param province - Province name
 * @returns List of districts in the province with count
 */
export async function getDistricts(
  province: string,
): Promise<Array<{ district: string; count: number }>> {
  try {
    const client = getSupabaseClient();

    const { data, error } = await client
      .from("accommodations")
      .select("district")
      .eq("province", province)
      .returns<Array<{ district: string }>>();

    if (error) {
      console.error("Failed to fetch districts:", error);
      throw new Error(`Failed to fetch districts: ${error.message}`);
    }

    // Count by district
    const districtCounts = (data ?? []).reduce(
      (acc, { district }) => {
        acc[district] = (acc[district] ?? 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    );

    return Object.entries(districtCounts)
      .map(([district, count]) => ({ district, count }))
      .sort((a, b) => a.district.localeCompare(b.district));
  } catch (error) {
    console.error("Error in getDistricts:", error);
    throw error;
  }
}

/**
 * Get accommodations for a specific course
 *
 * Retrieves all active accommodations that are associated with a course_id
 *
 * @param courseId - Course ID to filter accommodations
 * @returns List of accommodations for the course
 */
export async function getAccommodationsByCourseId(
  courseId: string,
): Promise<AccommodationWithDetails[]> {
  try {
    const client = getSupabaseClient();

    const { data, error } = await client
      .from("accommodations")
      .select(
        `
        accommodation_id:id,
        name,
        type_legacy,
        description,
        province,
        district,
        address,
        postal_code,
        latitude,
        longitude,
        phone,
        homepage_url,
        booking_url,
        map_url,
        price_from,
        capacity,
        check_in_time,
        check_out_time,
        amenities,
        features,
        has_pickup,
        has_kitchen,
        women_only,
        trail_name,
        course_number,
        is_visible,
        featured,
        is_partner,
        is_active,
        directions,
        notice,
        accommodation_type:accommodation_types(id, name),
        accommodation_images!left(
          image_id:id,
          accommodation_id,
          image_url,
          storage_path,
          image_order
        )
      `,
      )
      .eq("course_id", courseId)
      .eq("is_active", true)
      .eq("is_visible", true)
      .eq("accommodation_images.is_main_photo", true);

    if (error) {
      console.error(`Failed to fetch accommodations for course ${courseId}:`, error);
      throw new Error(`Failed to fetch accommodations: ${error.message}`);
    }

    // Transform data: extract main_image from accommodation_images array
    const accommodations = (data ?? []).map((item: any) => ({
      ...item,
      main_image: Array.isArray(item.accommodation_images)
        ? item.accommodation_images[0]
        : item.accommodation_images,
      accommodation_images: undefined, // Remove the array field
    })) as AccommodationWithDetails[];

    return accommodations;
  } catch (error) {
    console.error("Error in getAccommodationsByCourseId:", error);
    throw error;
  }
}

/**
 * Get course route geometry as GeoJSON
 *
 * Fetches the route_line from courses table and converts it to GeoJSON format
 * for display on Naver Maps as a Polyline
 *
 * @param courseId - Course ID
 * @returns GeoJSON LineString coordinates [[lng, lat], [lng, lat], ...]
 */
export async function getCourseRouteGeoJSON(
  courseId: string
): Promise<[number, number][] | null> {
  try {
    const client = getSupabaseClient();

    const { data, error } = await client
      .rpc('get_course_route_geojson', {
        p_course_id: courseId
      });

    if (error) {
      console.error(`Failed to fetch course route for ${courseId}:`, error);
      return null;
    }

    if (!data) {
      console.warn(`No route data found for course ${courseId}`);
      return null;
    }

    // Parse GeoJSON response
    // RPC returns GeoJSON object: { type: "LineString", coordinates: [[lng, lat, z], ...] }
    const geojson = data;

    if (!geojson.coordinates || !Array.isArray(geojson.coordinates)) {
      console.warn(`Invalid GeoJSON format for course ${courseId}:`, geojson);
      return null;
    }

    // Convert 3D coordinates [lng, lat, elevation] to 2D [lng, lat]
    const coordinates = geojson.coordinates.map((coord: number[]) => [
      coord[0], // longitude
      coord[1]  // latitude
    ]) as [number, number][];

    console.log(`Loaded ${coordinates.length} points for course ${courseId}`);

    return coordinates;
  } catch (error) {
    console.error("Error in getCourseRouteGeoJSON:", error);
    return null;
  }
}

/**
 * Get accommodations for homepage display
 *
 * Fetches a mix of Wholsee Stay (partner) and Recommended (featured) accommodations.
 * Returns a list that can be shuffled/randomized by the consumer.
 */
export async function getHomepageAccommodations(): Promise<AccommodationWithDetails[]> {
  try {
    const client = getSupabaseClient();

    // Fetch partners and featured accommodations
    // We fetch a bit more than needed to allow for randomization
    const { data, error } = await client
      .from("accommodations")
      .select(
        `
        accommodation_id:id,
        name,
        province,
        district,
        address,
        latitude,
        longitude,
        price_from,
        description,
        contact_phone:phone,
        website:homepage_url,
        map_url,
        amenities,
        is_partner,
        featured,
        trail_name,
        accommodation_type:accommodation_types(id, name),
        accommodation_images!left(
          image_id:id,
          accommodation_id,
          image_url,
          is_main_photo
        )
      `
      )
      .or('is_partner.eq.true,featured.eq.true')
      .eq('is_active', true)
      .eq('is_visible', true)
      .eq('accommodation_images.is_main_photo', true)
      .limit(50); // Fetch enough to randomize

    if (error) {
      console.error("Failed to fetch homepage accommodations:", error);
      throw new Error(`Failed to fetch homepage accommodations: ${error.message}`);
    }

    // Transform data
    const accommodations = (data ?? []).map((item: any) => ({
      ...item,
      main_image: Array.isArray(item.accommodation_images)
        ? item.accommodation_images[0]
        : item.accommodation_images,
      accommodation_images: undefined,
    })) as AccommodationWithDetails[];

    return accommodations;
  } catch (error) {
    console.error("Error in getHomepageAccommodations:", error);
    throw error;
  }
}
